import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';
import {
  Camera, Upload, Check, X, Trash2, Image, Loader2, ChevronLeft,
  Plus, Eye, RotateCcw, Send, FileText, Wifi, WifiOff
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

// ==================== MOBILE CAPTURE PAGE ====================
export default function MobileCapturePage() {
  const [searchParams] = useSearchParams();
  const workspaceId = searchParams.get('w') || '';
  const sessionId = searchParams.get('s') || '';

  const [session, setSession] = useState(null);
  const [capturedReceipts, setCapturedReceipts] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [captureMode, setCaptureMode] = useState('standard'); // standard, burst
  const [previewImage, setPreviewImage] = useState(null);
  const [online, setOnline] = useState(navigator.onLine);
  const [pendingUploads, setPendingUploads] = useState([]);
  const [note, setNote] = useState('');

  const cameraInputRef = useRef(null);
  const fileInputRef = useRef(null);

  // Online status tracking
  useEffect(() => {
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => { window.removeEventListener('online', handleOnline); window.removeEventListener('offline', handleOffline); };
  }, []);

  // Flush pending uploads when back online
  useEffect(() => {
    if (online && pendingUploads.length > 0) {
      flushPending();
    }
  }, [online]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch session info
  const fetchSession = useCallback(async () => {
    if (!workspaceId || !sessionId) return;
    try {
      const r = await fetch(`${API}/api/expense-capture/${workspaceId}/sessions/${sessionId}`, { credentials: 'include' });
      if (r.ok) {
        const data = await r.json();
        setSession(data);
        setCapturedReceipts(data.receipts || []);
      }
    } catch {}
  }, [workspaceId, sessionId]);

  useEffect(() => { fetchSession(); }, [fetchSession]);

  // Poll for session updates (live connection)
  useEffect(() => {
    if (!workspaceId || !sessionId) return;
    const interval = setInterval(fetchSession, 5000);
    return () => clearInterval(interval);
  }, [fetchSession, workspaceId, sessionId]);

  // Upload receipt
  const uploadReceipt = async (file, fileNote = '') => {
    if (!workspaceId || !sessionId) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('note', fileNote);
      const r = await fetch(`${API}/api/expense-capture/${workspaceId}/sessions/${sessionId}/upload`, {
        method: 'POST', credentials: 'include', body: form,
      });
      if (r.ok) {
        const receipt = await r.json();
        setCapturedReceipts(prev => [receipt, ...prev]);
        toast.success('Receipt uploaded');
        return true;
      } else {
        toast.error('Upload failed');
      }
    } catch (err) {
      // Queue for later if offline
      if (!navigator.onLine) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPendingUploads(prev => [...prev, { dataUrl: reader.result, name: file.name, note: fileNote, timestamp: Date.now() }]);
          toast.info('Saved offline — will upload when back online');
        };
        reader.readAsDataURL(file);
        return true;
      }
      toast.error('Upload error');
    } finally {
      setUploading(false);
    }
    return false;
  };

  const flushPending = async () => {
    if (pendingUploads.length === 0) return;
    const toUpload = [...pendingUploads];
    setPendingUploads([]);
    let uploaded = 0;
    for (const item of toUpload) {
      try {
        const resp = await fetch(item.dataUrl);
        const blob = await resp.blob();
        const file = new File([blob], item.name, { type: blob.type });
        const ok = await uploadReceipt(file, item.note);
        if (ok) uploaded++;
      } catch {}
    }
    if (uploaded > 0) toast.success(`Uploaded ${uploaded} queued receipt(s)`);
  };

  // Camera capture handler
  const handleCapture = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;

    if (captureMode === 'burst') {
      // Upload all files in sequence
      for (const file of files) {
        await uploadReceipt(file, note);
      }
      setNote('');
    } else {
      // Standard: show preview first
      const file = files[0];
      const url = URL.createObjectURL(file);
      setPreviewImage({ url, file, name: file.name });
    }
    // Reset input
    if (cameraInputRef.current) cameraInputRef.current.value = '';
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const confirmCapture = async () => {
    if (!previewImage) return;
    await uploadReceipt(previewImage.file, note);
    URL.revokeObjectURL(previewImage.url);
    setPreviewImage(null);
    setNote('');
  };

  const discardCapture = () => {
    if (previewImage) URL.revokeObjectURL(previewImage.url);
    setPreviewImage(null);
    setNote('');
  };

  // No session
  if (!workspaceId || !sessionId) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center space-y-3 max-w-xs">
          <Camera className="w-12 h-12 mx-auto text-muted-foreground" />
          <h1 className="text-lg font-bold">Orka Receipt Capture</h1>
          <p className="text-sm text-muted-foreground">Invalid capture link. Please scan the QR code from your Orka desktop app to start a capture session.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="mobile-capture-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-card border-b px-4 py-3 flex items-center justify-between safe-area-top">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
            <Camera className="w-4 h-4 text-white" />
          </div>
          <div>
            <h1 className="text-sm font-bold leading-tight">Receipt Capture</h1>
            <p className="text-[10px] text-muted-foreground">{session?.name || 'Loading...'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {online ? (
            <span className="flex items-center gap-1 text-[10px] text-green-600"><Wifi className="w-3 h-3" />Live</span>
          ) : (
            <span className="flex items-center gap-1 text-[10px] text-orange-500"><WifiOff className="w-3 h-3" />Offline</span>
          )}
          {pendingUploads.length > 0 && (
            <span className="px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded text-[10px] font-semibold">{pendingUploads.length} queued</span>
          )}
          <span className="px-1.5 py-0.5 bg-muted rounded text-[10px] font-semibold">{capturedReceipts.length} captured</span>
        </div>
      </header>

      {/* Capture Mode Toggle */}
      <div className="px-4 py-2 border-b bg-muted/30">
        <div className="flex gap-2">
          <button
            onClick={() => setCaptureMode('standard')}
            className={cn("flex-1 py-2 rounded-lg text-xs font-semibold transition-colors",
              captureMode === 'standard' ? "bg-card border shadow-sm" : "text-muted-foreground")}
            data-testid="mode-standard"
          >
            <Camera className="w-4 h-4 mx-auto mb-0.5" />Standard
          </button>
          <button
            onClick={() => setCaptureMode('burst')}
            className={cn("flex-1 py-2 rounded-lg text-xs font-semibold transition-colors",
              captureMode === 'burst' ? "bg-card border shadow-sm" : "text-muted-foreground")}
            data-testid="mode-burst"
          >
            <Plus className="w-4 h-4 mx-auto mb-0.5" />Burst
          </button>
        </div>
        <p className="text-[10px] text-muted-foreground text-center mt-1">
          {captureMode === 'standard' ? 'Preview before upload' : 'Rapid capture — uploads immediately'}
        </p>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Preview State */}
        {previewImage ? (
          <div className="p-4 space-y-3" data-testid="preview-panel">
            <div className="relative rounded-xl overflow-hidden border-2 border-primary/30 bg-black">
              <img src={previewImage.url} alt="Captured" className="w-full max-h-[50vh] object-contain" />
            </div>
            <input
              type="text"
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="Add a note (optional)"
              className="w-full h-9 px-3 text-sm border rounded-lg bg-background"
              data-testid="capture-note-input"
            />
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={discardCapture} data-testid="discard-btn">
                <X className="w-4 h-4 mr-1" />Discard
              </Button>
              <Button variant="outline" className="flex-1" onClick={() => cameraInputRef.current?.click()} data-testid="retake-btn">
                <RotateCcw className="w-4 h-4 mr-1" />Retake
              </Button>
              <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={confirmCapture} disabled={uploading} data-testid="confirm-upload-btn">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}
                Upload
              </Button>
            </div>
          </div>
        ) : (
          /* Capture Area */
          <div className="p-4 space-y-4">
            {/* Capture Buttons */}
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => cameraInputRef.current?.click()}
                className="aspect-square rounded-2xl border-2 border-dashed border-primary/30 bg-primary/5 flex flex-col items-center justify-center gap-2 active:scale-95 transition-transform"
                data-testid="camera-capture-btn"
              >
                <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                  <Camera className="w-7 h-7 text-primary" />
                </div>
                <span className="text-xs font-semibold">Take Photo</span>
                <span className="text-[10px] text-muted-foreground">Use camera</span>
              </button>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="aspect-square rounded-2xl border-2 border-dashed border-muted-foreground/20 bg-muted/30 flex flex-col items-center justify-center gap-2 active:scale-95 transition-transform"
                data-testid="gallery-upload-btn"
              >
                <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center">
                  <Image className="w-7 h-7 text-muted-foreground" />
                </div>
                <span className="text-xs font-semibold">From Gallery</span>
                <span className="text-[10px] text-muted-foreground">Pick photos</span>
              </button>
            </div>

            {/* Note input for burst mode */}
            {captureMode === 'burst' && (
              <input
                type="text"
                value={note}
                onChange={e => setNote(e.target.value)}
                placeholder="Session note (applies to all captures)"
                className="w-full h-9 px-3 text-sm border rounded-lg bg-background"
              />
            )}

            {/* Captured Receipts List */}
            {capturedReceipts.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-xs font-semibold flex items-center gap-1.5">
                  <FileText className="w-3.5 h-3.5" />
                  Captured This Session ({capturedReceipts.length})
                </h3>
                <div className="grid grid-cols-3 gap-2">
                  {capturedReceipts.map(r => (
                    <div key={r.receipt_id} className="rounded-lg border overflow-hidden bg-card" data-testid={`captured-${r.receipt_id}`}>
                      <div className="aspect-[3/4] bg-muted/30 flex items-center justify-center">
                        {r.file_url && ['jpg', 'jpeg', 'png', 'webp'].includes(r.file_type) ? (
                          <img src={`${API}${r.file_url}`} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <FileText className="w-6 h-6 text-muted-foreground" />
                        )}
                      </div>
                      <div className="p-1.5">
                        <p className="text-[10px] truncate">{r.extracted_data?.merchant_name || r.filename}</p>
                        <div className="flex items-center justify-between mt-0.5">
                          <span className={cn(
                            "text-[9px] px-1 py-0 rounded-full font-medium",
                            r.extraction_status === 'done' ? "bg-green-100 text-green-700" :
                            r.extraction_status === 'pending' ? "bg-slate-100 text-slate-600" :
                            "bg-red-100 text-red-700"
                          )}>
                            {r.extraction_status === 'done' ? 'Extracted' : r.extraction_status === 'pending' ? 'Queued' : 'Failed'}
                          </span>
                          {r.extracted_data?.total && (
                            <span className="text-[10px] font-mono font-semibold">{r.extracted_data.currency} {r.extracted_data.total}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Hidden inputs */}
      <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden"
        onChange={handleCapture} multiple={captureMode === 'burst'} />
      <input ref={fileInputRef} type="file" accept="image/*,.pdf" className="hidden"
        onChange={handleCapture} multiple={captureMode === 'burst'} />

      {/* Bottom status bar */}
      <div className="sticky bottom-0 bg-card border-t px-4 py-2 safe-area-bottom">
        <div className="flex items-center justify-between text-[10px] text-muted-foreground">
          <span>Session: {sessionId.slice(0, 12)}...</span>
          <span>{online ? 'Connected to Orka' : 'Offline mode'}</span>
        </div>
      </div>
    </div>
  );
}
